import { useState } from "react";
import { todayHot, type RankItem } from "@/mocks/gossipData";

const rankStyle = (rank: number) => {
  if (rank <= 3) return "text-primary-600 font-display";
  return "text-foreground-400 font-display";
};

const trendIcon = (trend: string) => {
  if (trend === "up") return <i className="ri-arrow-up-line text-accent-600"></i>;
  if (trend === "down") return <i className="ri-arrow-down-line text-secondary-600"></i>;
  if (trend === "new") return <i className="ri-sparkling-line text-primary-600"></i>;
  return <i className="ri-subtract-line text-foreground-300"></i>;
};

const tagClasses: Record<string, string> = {
  primary: "bg-primary-500 text-background-50",
  accent: "bg-accent-500 text-background-950",
  secondary: "bg-secondary-100 text-secondary-800",
  foreground: "bg-foreground-900 text-background-50",
};

export default function TodayHot() {
  const [showAll, setShowAll] = useState(false);
  const list = showAll ? todayHot : todayHot.slice(0, 8);
  const maxHeat = todayHot[0].heat;

  return (
    <section id="today" className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
              <i className="ri-fire-fill text-primary-600"></i>
              TODAY · 今日吃瓜精选
            </span>
            <h2 className="mt-2 font-display text-3xl md:text-4xl text-foreground-950">
              今日热门榜<span className="text-primary-600">·</span>热度TOP事件
            </h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              实时滚动聚合全网讨论最热烈的事件，从顶流动态到民生暖闻，这里就是当天的瓜田中心。
            </p>
          </div>
          <button
            onClick={() => setShowAll(!showAll)}
            className="whitespace-nowrap inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors self-start md:self-auto"
          >
            {showAll ? "收起榜单" : "展开完整TOP10"}
            <i className={`${showAll ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <ol className="divide-y divide-background-200 rounded-2xl bg-background-50 ring-1 ring-background-200 overflow-hidden">
              {list.map((item: RankItem) => (
                <li key={item.rank} className="group flex items-center gap-4 px-4 md:px-6 py-4 hover:bg-background-100/70 transition-colors cursor-pointer">
                  <span className={`w-8 text-center text-xl ${rankStyle(item.rank)}`}>{item.rank}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm md:text-[15px] font-medium text-foreground-950 leading-snug">
                        {item.title}
                      </p>
                      <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded px-1.5 text-[11px] font-bold ${tagClasses[item.tagType]}`}>
                        {item.tag}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex h-1.5 w-full max-w-xs rounded-full bg-background-200 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${item.rank <= 3 ? "bg-primary-500" : "bg-accent-400"}`}
                          style={{ width: `${(item.heat / maxHeat) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{item.heatText}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    <span className="rounded-md bg-background-200 px-2 py-1 text-[11px] text-foreground-600 whitespace-nowrap">{item.category}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{item.time}</span>
                    <span className="flex h-6 w-6 items-center justify-center">{trendIcon(item.trend)}</span>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-center text-xs text-foreground-500">
              数据口径：公开平台话题综合热度 · 排名随讨论量实时变动，仅供参考
            </p>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl bg-primary-50 ring-1 ring-primary-100 p-5 md:p-6">
              <h3 className="flex items-center gap-2 text-base font-bold text-primary-900 mb-4">
                <i className="ri-focus-3-line text-primary-600"></i>
                热度TOP看点
              </h3>
              <ul className="space-y-3.5">
                {todayHot.slice(0, 3).map((item) => (
                  <li key={item.rank} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xs font-bold">
                      {item.rank}
                    </span>
                    <p className="text-sm leading-snug text-foreground-800">{item.title}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-accent-100/80 to-secondary-100/60 p-5 md:p-6 ring-1 ring-background-200">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-3">
                <i className="ri-question-line text-accent-700"></i>
                什么是"吃瓜精选"？
              </h3>
              <p className="text-sm leading-relaxed text-foreground-700">
                吃瓜精选是一份每日滚动更新的热点追踪清单，帮你把散落各处的热门瓜料聚合到同一张榜单，快速判断今天全网在聊什么、热度峰值在哪里。
              </p>
              <a href="#about" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent-700 hover:text-accent-800">
                了解更多
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}